import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, ImagePlus, Trash2, Loader2 } from "lucide-react";
import api, { apiErr, assetUrl } from "@/lib/api";
import ThemePreviewModal from "@/components/app/ThemePreview";
import DataTable from "@/components/app/DataTable";
import { pageSub, pageTitle } from "@/components/app/ui";

const SWATCHES = ["primary", "accent", "bg", "surface", "text"];
const MAX_BYTES = 5 * 1024 * 1024;

/** Thumbnail of the theme's preview image with upload / replace / remove. */
function PreviewCell({ t, busy, onUpload, onRemove, onOpen }) {
  const input = useRef(null);
  const working = busy === `preview-${t.id}`;
  return (
    <div className="flex items-center gap-2.5 min-w-[190px]" onClick={(e) => e.stopPropagation()}>
      {t.preview_image
        ? <button type="button" onClick={() => onOpen(t)} data-testid={`theme-preview-thumb-${t.id}`} aria-label={`View ${t.name} preview`} className="w-[84px] h-[52px] rounded-md overflow-hidden border border-slate-200 shrink-0 hover:border-blue-400">
            <img src={assetUrl(t.preview_image)} alt="" loading="lazy" className="w-full h-full object-cover object-top" /></button>
        : <span className="w-[84px] h-[52px] rounded-md border border-dashed border-slate-300 text-[10px] text-slate-400 flex items-center justify-center shrink-0 text-center leading-tight px-1">No preview image</span>}
      <div className="flex flex-col items-start gap-1">
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid={`theme-preview-input-${t.id}`}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(t, f); }} />
        <button type="button" onClick={() => input.current?.click()} disabled={working} data-testid={`theme-preview-upload-${t.id}`}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline disabled:opacity-50">
          {working ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />} {t.preview_image ? "Replace" : "Upload"}
        </button>
        {t.preview_image && <button type="button" onClick={() => onRemove(t)} disabled={working} data-testid={`theme-preview-remove-${t.id}`} className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-red-600 disabled:opacity-50"><Trash2 size={12} /> Remove</button>}
      </div>
    </div>
  );
}

export default function AdminThemes() {
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => api.get("/admin/themes").then(({ data }) => { setThemes(data.themes); setCategories(data.categories); }).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (t) => {
    const enabled = !t.enabled;
    if (!enabled && !window.confirm(`Disable “${t.name}” for ${t.category}? Vendors can no longer pick it${t.stores ? `; ${t.stores} store(s) already using it keep it` : ""}.`)) return;
    setBusy(t.id);
    try {
      await api.put(`/admin/themes/${t.id}`, { enabled });
      toast.success(`“${t.name}” ${enabled ? "enabled" : "disabled"} for ${t.category}.`);
      setThemes((list) => list.map((x) => (x.id === t.id ? { ...x, enabled } : x)));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(null); }
  }, []);

  const uploadPreview = useCallback(async (t, file) => {
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) { toast.error("Choose a PNG, JPG, WebP or GIF image."); return; }
    if (file.size > MAX_BYTES) { toast.error("The image is too large (max 5 MB)."); return; }
    setBusy(`preview-${t.id}`);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/themes/${t.id}/preview`, fd);
      setThemes((list) => list.map((x) => (x.id === t.id ? { ...x, preview_image: data.preview_image } : x)));
      toast.success(`Preview image saved for “${t.name}”. Vendors will see it when choosing a theme.`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(null); }
  }, []);

  const removePreview = useCallback(async (t) => {
    if (!window.confirm(`Remove the preview image of “${t.name}”?`)) return;
    setBusy(`preview-${t.id}`);
    try {
      await api.delete(`/admin/themes/${t.id}/preview`);
      setThemes((list) => list.map((x) => (x.id === t.id ? { ...x, preview_image: null } : x)));
      toast.success("Preview image removed.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(null); }
  }, []);

  const columns = useMemo(() => [
    { key: "name", header: "Theme", cell: (t) => (
      <div className="flex items-center gap-3 min-w-[190px]">
        <span className="w-11 h-11 rounded-lg overflow-hidden border border-slate-200 grid grid-cols-2 shrink-0" aria-hidden>
          <span style={{ background: t.colors.primary }} /><span style={{ background: t.colors.accent }} /><span style={{ background: t.colors.bg }} /><span style={{ background: t.colors.surface }} />
        </span>
        <div><div className="font-semibold text-neutral-900">{t.name}</div><div className="text-[11px] text-slate-500 font-mono">{t.id}</div></div>
      </div>
    ) },
    { key: "preview", header: "Preview image (shown to vendors)", sortable: false, cell: (t) => <PreviewCell t={t} busy={busy} onUpload={uploadPreview} onRemove={removePreview} onOpen={setViewing} /> },
    { key: "category", header: "Store category", cell: (t) => <span className="inline-block text-[11px] px-2 py-0.5 rounded-md border bg-blue-50 text-blue-700 border-blue-200 font-medium">{t.category}</span> },
    { key: "colors", header: "Palette", sortable: false, hideBelow: "md", cell: (t) => (
      <div className="flex gap-1">{SWATCHES.map((k) => <span key={k} title={`${k} ${t.colors[k]}`} className="w-5 h-5 rounded-full border border-slate-200" style={{ background: t.colors[k] }} />)}</div>
    ) },
    { key: "font", header: "Font", hideBelow: "lg", cell: (t) => <span className="text-slate-600">{t.font}</span> },
    { key: "layout", header: "Layout", hideBelow: "lg", cell: (t) => <span className="text-slate-600 capitalize">{t.layout}</span> },
    { key: "stores", header: "Stores using", align: "center", accessor: (t) => t.stores },
    { key: "enabled", header: "Status", accessor: (t) => (t.enabled ? "enabled" : "disabled"), cell: (t) => (
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium ${t.enabled ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{t.enabled ? "Enabled" : "Disabled"}</span>
    ) },
    { key: "actions", header: "Available to vendors", sortable: false, align: "right", cell: (t) => (
      <button role="switch" aria-checked={t.enabled} aria-label={`${t.enabled ? "Disable" : "Enable"} ${t.name}`} disabled={busy === t.id} onClick={(e) => { e.stopPropagation(); toggle(t); }} data-testid={`theme-toggle-${t.id}`}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${t.enabled ? "bg-green-500" : "bg-slate-300"}`}>
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${t.enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
    ) },
  ], [busy, toggle, uploadPreview, removePreview]);

  const filters = useMemo(() => [
    { key: "category", label: "All store categories", accessor: (t) => t.category, options: categories.map((c) => ({ value: c, label: c })) },
    { key: "status", label: "All status", accessor: (t) => (t.enabled ? "enabled" : "disabled"), options: [{ value: "enabled", label: "Enabled" }, { value: "disabled", label: "Disabled" }] },
  ], [categories]);

  const stats = [
    { label: "Total themes", value: themes.length, tone: "from-blue-50 to-white border-blue-100" },
    { label: "Enabled", value: themes.filter((t) => t.enabled).length, tone: "from-green-50 to-white border-green-100" },
    { label: "Disabled", value: themes.filter((t) => !t.enabled).length, tone: "from-orange-50 to-white border-orange-100" },
    { label: "Stores using a theme", value: themes.reduce((n, t) => n + t.stores, 0), tone: "from-blue-50 to-white border-blue-100" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-themes">
      <div>
        <h1 className={pageTitle}>Themes</h1>
        <p className={pageSub}>Choose which themes vendors can pick for each store category. Disabling a theme hides it from new selections; stores already using it keep it.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-xl border bg-gradient-to-br ${s.tone} px-4 py-3`}>
            <div className="text-[12px] text-slate-500">{s.label}</div>
            <div className="text-[22px] font-bold text-neutral-900 leading-tight">{s.value}</div>
          </div>
        ))}
      </div>
      <DataTable testId="themes-table" exportName="themes" columns={columns} data={themes} loading={loading} rowKey="id" rowTestId={(t) => `theme-row-${t.id}`}
        searchKeys={["name", "id", "category", "font"]} searchPlaceholder="Search themes…" filters={filters} defaultSort={{ key: "category", dir: "asc" }}
        emptyIcon={Palette} emptyTitle="No themes" emptyText="There are no themes to show." />
      <p className="text-[12px] text-slate-500">Preview images: upload a screenshot of the theme&apos;s homepage (PNG, JPG, WebP or GIF, up to 5 MB; about 1440 px wide works well). Vendors see it on the theme card and can open it full size before choosing the theme.</p>
      <ThemePreviewModal theme={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
